<template>
    <div class="qi-page">
        <a href="/invoices" class="btn btn-sm btn-outline-secondary mb-2"><i class="bi bi-arrow-left"></i> Back to Invoices</a>

        <div v-if="loading" class="text-center py-5"><span class="spinner-border"></span></div>

        <div v-else-if="invoice">
            <div class="qi-doc-titlebar d-flex justify-content-between flex-wrap align-items-start">
                <h4 class="mb-0">{{ company.name }} <span class="text-muted">#{{ invoice.number }}</span></h4>
                <div class="qi-header-actions" style="gap:8px;">
                    <div class="dropdown">
                        <button class="btn btn-outline-primary rounded dropdown-toggle" type="button" data-toggle="dropdown" aria-expanded="false">More actions</button>
                        <div class="dropdown-menu dropdown-menu-right">
                            <button type="button" class="dropdown-item" :disabled="downloadingPdf" @click="downloadPdfFile"><i class="bi bi-file-earmark-pdf mr-2"></i>Export as PDF</button>
                            <button type="button" class="dropdown-item" :disabled="printingPdf" @click="printPdfFile"><i class="bi bi-printer mr-2"></i>Print</button>
                            <div class="dropdown-divider"></div>
                            <a class="dropdown-item" href="/settings/receipt"><i class="bi bi-palette mr-2"></i>Customize</a>
                            <button type="button" class="dropdown-item" @click="showPreview = true"><i class="bi bi-eye mr-2"></i>Preview as customer</button>
                            <a v-if="canEdit" class="dropdown-item" :href="`/invoices/${invoice.id}/edit`"><i class="bi bi-pencil mr-2"></i>Edit invoice</a>
                            <template v-if="canVoid">
                                <div class="dropdown-divider"></div>
                                <button type="button" class="dropdown-item text-danger" @click="voidInvoice"><i class="bi bi-x-circle mr-2"></i>Void invoice</button>
                            </template>
                        </div>
                    </div>
                    <a href="/invoices/create" class="btn btn-outline-primary rounded">Create another Invoice</a>
                </div>
            </div>

            <div class="qi-doc-meta-row">
                <div>
                    <label class="qi-mini-label">Status</label>
                    <span class="badge" :class="statusMeta.badge">{{ statusMeta.label }}</span>
                </div>
                <div>
                    <label class="qi-mini-label">Customer</label>
                    <div class="font-weight-bold">{{ customerLabel(invoice.customer) }}</div>
                    <small v-if="customerAttnLine(invoice.customer)" class="text-muted">Attn: {{ customerAttnLine(invoice.customer) }}</small>
                    <small v-if="invoice.customer && invoice.customer.phone" class="text-muted d-block">{{ invoice.customer.phone }}</small>
                    <small v-if="invoice.customer && invoice.customer.full_address" class="text-muted d-block">{{ invoice.customer.full_address }}</small>
                </div>
                <div>
                    <label class="qi-mini-label">Amount due</label>
                    <div class="font-weight-bold">{{ formatMoney(invoice.balance) }}</div>
                </div>
                <div v-if="invoice.due_date">
                    <label class="qi-mini-label">Due on</label>
                    <div class="font-weight-bold">{{ formatDate(invoice.due_date) }}</div>
                </div>
            </div>

            <div class="qi-stepper">
                <div class="qi-step qi-step-done">
                    <div class="qi-step-icon"><i class="bi bi-file-earmark-text"></i></div>
                    <div class="qi-step-body">
                        <div class="qi-step-title">Create</div>
                        <div class="text-muted small">
                            Created {{ formatDate(invoice.date) }}
                            <span v-if="invoice.quote_id"> from a <a :href="`/quotes/${invoice.quote_id}`">quote</a></span>
                        </div>
                    </div>
                </div>
                <div class="qi-step" :class="{ 'qi-step-done': invoice.sent_at }">
                    <div class="qi-step-icon"><i class="bi bi-send"></i></div>
                    <div class="qi-step-body">
                        <div class="qi-step-title">Send</div>
                        <div class="text-muted small">Last sent: {{ invoice.sent_at ? formatDate(invoice.sent_at) : 'Never' }}</div>
                    </div>
                    <button type="button" class="btn btn-primary btn-sm" @click="openSendModal"><i class="bi bi-send mr-1"></i>Send</button>
                </div>
                <div class="qi-step" :class="{ 'qi-step-done': invoice.status === 'paid' }">
                    <div class="qi-step-icon"><i class="bi bi-credit-card"></i></div>
                    <div class="qi-step-body">
                        <div class="qi-step-title">Manage payments</div>
                        <div class="text-muted small">Amount due: {{ formatMoney(invoice.balance) }}</div>
                    </div>
                    <button v-if="invoice.balance > 0 && invoice.status !== 'cancelled'" type="button" class="btn btn-primary btn-sm" @click="showPaymentModal = true"><i class="bi bi-cash-coin mr-1"></i>Record Payment</button>
                </div>
                <div class="qi-step" v-if="!['cancelled', 'draft'].includes(invoice.status) && (invoice.items || []).some(i => i.product_id)">
                    <div class="qi-step-icon"><i class="bi bi-truck"></i></div>
                    <div class="qi-step-body">
                        <div class="qi-step-title">Deliver goods</div>
                        <div class="text-muted small">Stock leaves when you dispatch a delivery note</div>
                    </div>
                    <a :href="`/deliveries?invoice=${invoice.id}`" class="btn btn-primary btn-sm"><i class="bi bi-truck mr-1"></i>Create delivery note</a>
                </div>
                <div class="qi-step" v-if="enableTims" :class="{ 'qi-step-done': fiscalStatus === 'completed' }">
                    <div class="qi-step-icon"><i class="bi bi-shield-check"></i></div>
                    <div class="qi-step-body">
                        <div class="qi-step-title">eTIMS / KRA Fiscal</div>
                        <div class="text-muted small">
                            <span v-if="fiscalStatus === 'completed'">Fiscalized<span v-if="invoice.fiscal_transaction.control_invoice_number"> — Control No: {{ invoice.fiscal_transaction.control_invoice_number }}</span></span>
                            <span v-else-if="fiscalStatus === 'processing' || fiscalStatus === 'pending'">{{ fiscalStatusMessage || 'Submitting to KRA…' }}</span>
                            <span v-else-if="fiscalStatus === 'failed'">Fiscal submission failed{{ invoice.fiscal_transaction.failure_reason ? ' — ' + invoice.fiscal_transaction.failure_reason : '' }}</span>
                            <span v-else-if="fiscalStatus === 'policy_blocked'">Blocked by company eTIMS submission policy</span>
                            <span v-else>Not yet generated</span>
                        </div>
                    </div>
                    <button v-if="canGenerateFiscal" type="button" class="btn btn-outline-primary btn-sm" :disabled="fiscalLoading" @click="generateFiscalReceipt">
                        <span v-if="fiscalLoading" class="spinner-border spinner-border-sm mr-1"></span>Generate eTIMS Compliant PDF
                    </button>
                    <button v-if="canIssueCreditNote" type="button" class="btn btn-outline-warning btn-sm" :disabled="fiscalLoading" @click="issueCreditNote">
                        <span v-if="fiscalLoading" class="spinner-border spinner-border-sm mr-1"></span>Issue Credit Note
                    </button>
                </div>
            </div>

            <div class="qi-form-card">
            <div class="qi-company-header">
                <div class="qi-company-brand">
                    <img v-if="showLogo && company.logo_url" :src="company.logo_url" :alt="company.name" class="qi-company-logo">
                    <div>
                        <div class="font-weight-bold">{{ company.name }}</div>
                        <small class="d-block font-weight-bold font-italic" v-if="company.companysettings && company.companysettings.document_tagline">{{ company.companysettings.document_tagline }}</small>
                        <small class="text-muted d-block" v-if="company.companysettings && company.companysettings.document_description">{{ company.companysettings.document_description }}</small>
                        <small class="text-muted d-block" v-if="company.phone || company.email">
                            <span v-if="company.phone">{{ company.phone }}</span><span v-if="company.phone && company.email"> &middot; </span><span v-if="company.email">{{ company.email }}</span>
                        </small>
                        <small class="text-muted d-block" v-if="companyAddress">{{ companyAddress }}</small>
                        <small class="text-muted d-block" v-if="company.krapin">PIN: {{ company.krapin }}</small>
                    </div>
                </div>
            </div>

            <div class="table-responsive d-none d-md-block">
                <table class="table table-sm qi-items-table">
                    <thead><tr><th>Item</th><th>Qty</th><th>Price</th><th>Discount</th><th>Tax</th><th class="text-right">Total</th></tr></thead>
                    <tbody>
                        <tr v-for="item in invoice.items" :key="item.id">
                            <td>
                                {{ item.description }}
                                <small v-if="item.item_note" class="text-muted d-block">{{ item.item_note }}</small>
                            </td>
                            <td>{{ item.quantity }}</td>
                            <td>{{ formatMoney(item.unit_price) }}</td>
                            <td>{{ formatMoney(item.discount) }}</td>
                            <td>{{ itemTaxLabel(item) }}</td>
                            <td class="text-right">{{ formatMoney(item.total) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="d-md-none">
                <div class="qi-item-card" v-for="item in invoice.items" :key="item.id">
                    <div class="d-flex justify-content-between">
                        <span class="font-weight-bold">{{ item.description }}</span>
                        <span class="font-weight-bold">{{ formatMoney(item.total) }}</span>
                    </div>
                    <div v-if="item.item_note" class="text-muted small">{{ item.item_note }}</div>
                    <div class="text-muted small">
                        {{ item.quantity }} &times; {{ formatMoney(item.unit_price) }}
                        <span v-if="item.discount > 0"> &middot; -{{ formatMoney(item.discount) }} discount</span>
                        <span v-if="itemTaxLabel(item) !== '—'"> &middot; {{ itemTaxLabel(item) }}</span>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-md-7">
                    <div v-if="invoice.notes" class="mb-2"><label class="qi-mini-label">Notes</label><p class="mb-0">{{ invoice.notes }}</p></div>
                    <div v-if="invoice.terms" class="mb-3"><label class="qi-mini-label">Terms</label><p class="mb-0">{{ invoice.terms }}</p></div>

                    <div v-if="invoice.payments && invoice.payments.length">
                        <label class="qi-mini-label">Payment history</label>
                        <div class="qi-payment-row" v-for="p in invoice.payments" :key="p.id">
                            <span>{{ formatDate(p.created_at) }} &middot; {{ p.method || 'Payment' }}<span v-if="p.reference"> ({{ p.reference }})</span></span>
                            <span class="font-weight-bold text-primary">{{ formatMoney(p.amount) }}</span>
                        </div>
                    </div>
                </div>
                <div class="col-md-5">
                    <div class="qi-totals-box">
                        <div class="d-flex justify-content-between"><span>Subtotal</span><span>{{ formatMoney(invoice.subtotal) }}</span></div>
                        <div class="d-flex justify-content-between text-muted"><span>Discount</span><span>-{{ formatMoney(invoice.discount) }}</span></div>
                        <div class="d-flex justify-content-between text-muted"><span>Tax</span><span>{{ formatMoney(invoice.tax_amount) }}</span></div>
                        <hr class="my-2">
                        <div class="d-flex justify-content-between font-weight-bold h6"><span>Total</span><span>{{ formatMoney(invoice.total_amount) }}</span></div>
                        <div class="d-flex justify-content-between text-primary"><span>Paid</span><span>{{ formatMoney(invoice.paid_amount) }}</span></div>
                        <div class="d-flex justify-content-between font-weight-bold" :class="invoice.balance > 0 ? 'text-danger' : 'text-primary'">
                            <span>Balance</span><span>{{ formatMoney(invoice.balance) }}</span>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </div>

        <!-- Record payment modal -->
        <div v-if="showPaymentModal" class="qi-modal-backdrop" @click.self="showPaymentModal = false">
            <div class="qi-modal-card">
                <div class="qi-modal-header">
                    <h6 class="mb-0">Record Payment</h6>
                    <button type="button" class="btn btn-sm btn-link text-muted p-0" @click="showPaymentModal = false"><i class="bi bi-x-lg"></i></button>
                </div>
                <div class="qi-modal-body">
                    <p class="text-muted small mb-2">Outstanding balance: <strong>{{ formatMoney(invoice.balance) }}</strong></p>
                    <div class="form-group mb-2">
                        <label class="small mb-1">Amount</label>
                        <input type="number" step="0.01" min="0.01" class="form-control" v-model.number="paymentForm.amount">
                    </div>
                    <div class="form-group mb-2">
                        <label class="small mb-1">Payment method</label>
                        <select class="form-control" v-model="paymentForm.payment_type_id">
                            <option :value="null">Select payment method</option>
                            <option v-for="pt in paymentTypes" :key="pt.id" :value="pt.id">{{ pt.payment_method }}</option>
                        </select>
                    </div>
                    <div class="form-group mb-0">
                        <label class="small mb-1">Reference <span class="text-muted">(optional)</span></label>
                        <input type="text" class="form-control" v-model="paymentForm.reference" placeholder="M-Pesa code, cheque no., etc.">
                    </div>
                    <div v-if="paymentError" class="alert alert-danger py-2 px-3 mt-2 mb-0 small">{{ paymentError }}</div>
                </div>
                <div class="qi-modal-footer">
                    <button type="button" class="btn btn-outline-secondary btn-sm" @click="showPaymentModal = false">Cancel</button>
                    <button type="button" class="btn btn-primary btn-sm" :disabled="recording || !paymentForm.amount || !paymentForm.payment_type_id" @click="recordPayment">
                        <span v-if="recording" class="spinner-border spinner-border-sm mr-1"></span> Save Payment
                    </button>
                </div>
            </div>
        </div>

        <document-preview-modal
            v-if="showPreview"
            doc-type="Invoice"
            :customer="invoice.customer"
            :items="invoice.items"
            :notes="invoice.notes"
            :terms="invoice.terms"
            :date="invoice.date"
            :expiry-date="invoice.due_date"
            :totals="{ subtotal: invoice.subtotal, discount: invoice.discount, tax: invoice.tax_amount, total: invoice.total_amount }"
            :currency="currency"
            saved
            @close="showPreview = false"
        ></document-preview-modal>

        <send-document-modal
            v-if="invoice"
            ref="sendModal"
            doc-type="Invoice"
            :doc-id="invoiceId"
            :number="invoice.number"
            :customer="invoice.customer"
            @sent="onSent"
        ></send-document-modal>
    </div>
</template>

<script>
import DocumentPreviewModal from './DocumentPreviewModal.vue';
import SendDocumentModal from './SendDocumentModal.vue';
import { formatMoney, invoiceStatusMeta, customerLabel, customerAttnLine, apiErrorMessage } from './docHelpers';
import { downloadPdf, printPdf } from '../../services/pdfDownload';

export default {
    name: 'InvoiceView',
    components: { DocumentPreviewModal, SendDocumentModal },
    props: {
        invoiceId: { type: String, required: true },
    },
    data() {
        return {
            loading: true,
            invoice: null,
            paymentTypes: [],
            showPaymentModal: false,
            showPreview: false,
            downloadingPdf: false,
            printingPdf: false,
            recording: false,
            paymentError: '',
            paymentForm: { amount: 0, payment_type_id: null, reference: '' },
            fiscalLoading: false,
            fiscalStatusMessage: '',
            fiscalPollingInterval: null,
        };
    },
    computed: {
        currency() {
            const u = this.$store.getters.userDetails;
            return (u && u.company && u.company.companysettings && u.company.companysettings.currency) || 'KSh';
        },
        company() {
            const u = this.$store.getters.userDetails;
            return (u && u.company) || {};
        },
        showLogo() {
            const cs = this.company.companysettings;
            return !cs || cs.document_show_logo !== false;
        },
        companyAddress() {
            return [this.company.building, this.company.street, this.company.address, this.company.town].filter(Boolean).join(', ');
        },
        statusMeta() { return invoiceStatusMeta(this.invoice ? this.invoice.status : 'unpaid'); },
        canEdit() { return this.invoice && this.invoice.status !== 'cancelled' && Number(this.invoice.paid_amount) === 0; },
        canVoid() { return this.invoice && this.invoice.status !== 'cancelled' && Number(this.invoice.paid_amount) === 0; },
        pdfUrl() { return `/api/invoices/${this.invoiceId}/pdf`; },
        enableTims() {
            const cs = this.company.companysettings;
            return !!(cs && cs.enable_tims);
        },
        fiscalStatus() {
            return this.invoice?.fiscal_transaction?.status || null;
        },
        canGenerateFiscal() {
            const ft = this.invoice?.fiscal_transaction;
            if (!ft) return true;
            return ft.status === 'failed';
        },
        canIssueCreditNote() {
            const ft = this.invoice?.fiscal_transaction;
            if (!ft || ft.status !== 'completed') return false;
            if (ft.transaction_type === 'credit_note') return false;
            const all = this.invoice?.fiscal_transactions || [];
            return !all.some(t => t.transaction_type === 'credit_note' && t.status === 'completed');
        },
    },
    created() {
        this.load();
        axios.get('/api/payments', { params: { enabled: true } }).then(({ data }) => {
            this.paymentTypes = Array.isArray(data) ? data.filter(p => p.enabled) : [];
        }).catch(() => {});
    },
    methods: {
        customerLabel,
        customerAttnLine,
        formatMoney(v) { return formatMoney(v, this.currency); },
        formatDate(d) { return d ? new Date(d).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; },
        itemTaxLabel(item) {
            // Prefer the immutable snapshot taken at document-creation time;
            // fall back to the live tax relation only for rows saved before
            // tax_name/tax_rate existed.
            if (item.tax_name) return `${item.tax_name} (${item.tax_rate}%)`;
            if (item.tax) return `${item.tax.tax} (${item.tax.tax_perc}%)`;
            return '—';
        },
        async load() {
            this.loading = true;
            try {
                const { data } = await axios.get(`/api/invoices/${this.invoiceId}`);
                this.invoice = data;
                this.paymentForm.amount = Number(data.balance) || 0;
            } catch (e) {
                this.$toasts.push({ type: 'danger', message: apiErrorMessage(e, 'Could not load this invoice.') });
            } finally {
                this.loading = false;
            }
        },
        async recordPayment() {
            this.recording = true;
            this.paymentError = '';
            try {
                await axios.post(`/api/invoices/${this.invoiceId}/payments`, this.paymentForm);
                this.$toasts.push({ type: 'success', message: 'Payment recorded.' });
                this.showPaymentModal = false;
                this.paymentForm = { amount: 0, payment_type_id: null, reference: '' };
                this.load();
            } catch (e) {
                this.paymentError = apiErrorMessage(e, 'Could not record this payment.');
            } finally {
                this.recording = false;
            }
        },
        async voidInvoice() {
            if (!confirm('Void this invoice? This cannot be undone.')) return;
            try {
                await axios.delete(`/api/invoices/${this.invoiceId}`);
                this.$toasts.push({ type: 'success', message: 'Invoice voided.' });
                this.load();
            } catch (e) {
                this.$toasts.push({ type: 'danger', message: apiErrorMessage(e, 'Could not void this invoice.') });
            }
        },
        openSendModal() {
            this.$refs.sendModal.open();
        },
        onSent(sentAt) {
            this.invoice.sent_at = sentAt;
        },
        async downloadPdfFile() {
            this.downloadingPdf = true;
            try {
                await downloadPdf(this.pdfUrl, `Invoice-${this.invoice.number}.pdf`);
            } catch (e) {
                this.$toasts.push({ type: 'danger', message: 'Could not download the PDF. Please try again.' });
            } finally {
                this.downloadingPdf = false;
            }
        },
        async printPdfFile() {
            this.printingPdf = true;
            try {
                await printPdf(this.pdfUrl);
            } catch (e) {
                this.$toasts.push({ type: 'danger', message: 'Could not print this invoice. Please try again.' });
            } finally {
                this.printingPdf = false;
            }
        },

        // ── eTIMS / KRA fiscal ───────────────────────────────────────────

        async generateFiscalReceipt() {
            if (this.fiscalLoading) return;
            this.fiscalLoading = true;
            try {
                const type = this.invoice?.customer?.krapin ? 'invoice' : 'sale';
                const { data } = await axios.post(`/api/fiscal/invoices/${this.invoiceId}/receipt`, { transaction_type: type });
                if (data.success) {
                    this.$toasts.push({ type: 'success', message: data.message || 'Fiscal receipt queued for processing.' });
                    this._pollFiscalStatus();
                } else {
                    this.$toasts.push({ type: 'danger', message: data.message || 'Failed to generate fiscal receipt.' });
                    this.fiscalLoading = false;
                }
            } catch (e) {
                this.$toasts.push({ type: 'danger', message: apiErrorMessage(e, 'Failed to generate fiscal receipt.') });
                this.fiscalLoading = false;
            }
        },

        async issueCreditNote() {
            if (this.fiscalLoading) return;
            if (!confirm('Issue a fiscal credit note to reverse this invoice\'s fiscal receipt? This cannot be undone.')) return;
            this.fiscalLoading = true;
            try {
                const { data } = await axios.post(`/api/fiscal/invoices/${this.invoiceId}/credit-note`);
                if (data.success) {
                    this.$toasts.push({ type: 'success', message: data.message || 'Credit note queued for processing.' });
                    this._pollFiscalStatus();
                } else {
                    this.$toasts.push({ type: 'danger', message: data.message || 'Failed to issue credit note.' });
                    this.fiscalLoading = false;
                }
            } catch (e) {
                this.$toasts.push({ type: 'danger', message: apiErrorMessage(e, 'Failed to issue credit note.') });
                this.fiscalLoading = false;
            }
        },

        // Poll the generic fiscal-status endpoint (keyed by the fiscalable's
        // own id, not the transaction uuid) until the job reaches a terminal
        // state, then reload the invoice so fiscal_transaction/the PDF stamp
        // reflect the final result.
        _pollFiscalStatus() {
            let attempts = 0;
            const MAX = 15;
            this.fiscalStatusMessage = 'Submitting to KRA…';
            if (this.fiscalPollingInterval) clearInterval(this.fiscalPollingInterval);

            this.fiscalPollingInterval = setInterval(async () => {
                attempts++;
                try {
                    const { data } = await axios.get(`/api/fiscal/receipts/${this.invoiceId}`);
                    const fd = data.data;
                    const terminal = fd && (fd.status === 'completed' || fd.status === 'failed' || fd.status === 'policy_blocked');
                    if (terminal || attempts >= MAX) {
                        clearInterval(this.fiscalPollingInterval);
                        this.fiscalPollingInterval = null;
                        this.fiscalLoading = false;
                        this.fiscalStatusMessage = '';
                        this.load();
                    } else {
                        this.fiscalStatusMessage = `Submitting to KRA… (${attempts}/${MAX})`;
                    }
                } catch (e) {
                    if (attempts >= MAX) {
                        clearInterval(this.fiscalPollingInterval);
                        this.fiscalPollingInterval = null;
                        this.fiscalLoading = false;
                        this.fiscalStatusMessage = '';
                        this.load();
                    }
                }
            }, 2000);
        },
    },
    beforeDestroy() {
        if (this.fiscalPollingInterval) {
            clearInterval(this.fiscalPollingInterval);
            this.fiscalPollingInterval = null;
        }
    },
};
</script>

<style scoped>
/* A global unscoped rule elsewhere in the app (employee_earning.vue) sets
   `.small { margin: 150px auto }`, which leaks onto every `.small` element
   site-wide. Neutralize it here (higher specificity than the lone class). */
.small { margin: 0; }
.qi-page { padding: 16px; max-width: 1000px; margin: 0 auto; }
@media (max-width: 768px) { .qi-page { padding-bottom: 90px; } }
.qi-form-card { background: #fff; border: 1px solid #eee; border-radius: 10px; padding: 20px; }
.qi-doc-titlebar { margin-bottom: 14px; }
.qi-header-actions { display: flex; flex-wrap: wrap; }
.qi-doc-meta-row {
    display: flex;
    gap: 40px;
    flex-wrap: wrap;
    background: #fff;
    border: 1px solid #eee;
    border-radius: 10px;
    padding: 14px 18px;
    margin-bottom: 14px;
}
.qi-stepper { display: flex; flex-direction: column; gap: 8px; margin-bottom: 18px; }
.qi-step {
    display: flex;
    align-items: center;
    gap: 14px;
    background: #fff;
    border: 1px solid #eee;
    border-radius: 10px;
    padding: 12px 16px;
}
.qi-step-icon {
    width: 34px; height: 34px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    background: #f1f2f4; color: #adb5bd; font-size: 1rem; flex-shrink: 0;
}
.qi-step-done .qi-step-icon { background: rgba(253,71,25,.12); color: #fd4719; }
.qi-step-body { flex: 1; }
.qi-step-title { font-weight: 600; font-size: .92rem; }
.qi-company-header { padding-bottom: 14px; margin-bottom: 14px; border-bottom: 1px solid #eee; }
.qi-company-brand { display: flex; align-items: center; gap: 12px; }
.qi-company-logo { height: 40px; width: auto; max-width: 140px; object-fit: contain; }
.qi-action-bar { display: flex; gap: 6px; flex-wrap: wrap; }
.qi-mini-label { font-size: .72rem; text-transform: uppercase; color: #6c757d; display: block; }
.qi-items-table th { font-size: .75rem; text-transform: uppercase; color: #6c757d; border-top: none; }
.qi-item-card { border: 1px solid #eee; border-radius: 8px; padding: 10px 12px; margin-bottom: 8px; }
.qi-totals-box { background: #f8f9fa; border-radius: 8px; padding: 14px 16px; }
.qi-payment-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #f2f2f2; font-size: .9rem; }

.qi-modal-backdrop {
    position: fixed; inset: 0; z-index: 1080; background: rgba(0,0,0,.4);
    display: flex; align-items: center; justify-content: center; padding: 16px;
}
.qi-modal-card { background: #fff; border-radius: 10px; width: 100%; max-width: 380px; box-shadow: 0 12px 40px rgba(0,0,0,.25); overflow: hidden; }
.qi-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; border-bottom: 1px solid #eee; }
.qi-modal-body { padding: 16px; }
.qi-modal-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 16px; border-top: 1px solid #eee; background: #fafafa; }
</style>
